import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Utility function to format decimal values (copied from StudentSingleResult.jsx)
const formatScore = (value) => {
  if (value === undefined || value === null || value === '') return '-';

  if (value === 0 || value === 0.0 || value === '0' || value === '0.0') {
    return '-';
  }

  const num = parseFloat(value);
  if (isNaN(num)) return '-';

  if (num % 1 === 0) {
    return Math.floor(num);
  }

  return num;
};

// Styles (copied from StudentSingleResult.jsx so layout matches single PDF)
const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 5,
    borderBottom: '1pt solid #1e40af',
    paddingBottom: 5,
  },
  logo: {
    width: 50,
    height: 50,
    margin: '0 auto 3',
  },
  schoolName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e40af',
    marginVertical: 3,
  },
  schoolInfo: {
    fontSize: 8,
    marginTop: 3,
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginVertical: 5,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  subjectTableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectNameCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
  },
  subjectNameCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
  },
  tableHeaderCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
    width: '6.67%',
  },
  tableHeaderCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
    width: '6.67%',
  },
  tableHeaderSubjectNameCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
  },
  psychomotorGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginVertical: 5,
  },
  psychomotorSkillBox: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 2,
    marginRight: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  psychomotorSkillName: {
    fontSize: 8,
    fontWeight: 'bold',
    marginRight: 8,
  },
  psychomotorSkillValue: {
    fontSize: 8,
    textAlign: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 1,
    width: 20,
    height: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 5,
  },
  fullWidthTableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '100%',
  },
  fullWidthTableCellLast: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '100%',
  },
  stamp: {
    textAlign: 'center',
    marginVertical: 0,
  },
  stampImage: {
    width: 60,
    margin: '0 auto',
  },
});

const formatRank = (position) => {
  if (!position) return 'N/A';
  const lastDigit = position % 10;
  const lastTwoDigits = position % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${position}th`;
  }

  switch (lastDigit) {
    case 1: return `${position}st`;
    case 2: return `${position}nd`;
    case 3: return `${position}rd`;
    default: return `${position}th`;
  }
};

const getTermlyPositionText = (resultData) => {
  const term = resultData?.term;
  const termPositions = resultData?.termPositions;
  if (!term || !termPositions) return 'N/A';

  switch (term.toLowerCase()) {
    case 'first':
      return formatRank(termPositions.first_term_position);
    case 'second':
      return formatRank(termPositions.second_term_position);
    case 'third':
      return formatRank(termPositions.third_term_position);
    default:
      return 'N/A';
  }
};

const ClassBatchResultPDF = ({ results }) => (
  <Document>
    {results.map((resultData, index) => (
      <Page key={resultData?.student?.id || index} size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src="/images/logo.jpg" style={styles.logo} />
          <Text style={styles.schoolName}>Unique & Fine International Schools, Enugu</Text>
          <Text style={styles.schoolInfo}>
            Postal Address: Trans-Aerodrome Emene (Beside Adoration Poultry), Enugu
          </Text>
          <Text style={styles.schoolInfo}>
            Email: info@uniqueandfineinternationalschools.com | Phone: 08156798185 | Website: www.uniqueandfineinternationalschools.com
          </Text>
        </View>

        {/* Student Information */}
        <View style={styles.table}>
          {[
            [
              { text: `NAME OF STUDENT: ${resultData?.student?.fullname || '-'}`, width: '33.33%' },
              { text: `CLASS: ${resultData?.class?.name || '-'}`, width: '33.33%' },
              { text: `NO. IN CLASS: ${resultData?.class?.total_students || '-'}`, width: '33.33%' },
            ],
            [
              { text: `ADMISSION NO: ${resultData?.student?.admission_no || '-'}`, width: '33.33%' },
              { text: `TERM: ${resultData?.term || '-'}`, width: '33.33%' },
              { text: `SESSION: ${resultData?.session || '-'}`, width: '33.33%' },
            ],
            [
              { text: `AVERAGE SCORE: ${resultData?.summary?.average_score || '-'}`, width: '33.33%' },
              { text: `TOTAL MARKS: ${resultData?.summary?.total_marks || '-'}`, width: '33.33%' },
              { text: `TOTAL OBTAINABLE: ${resultData?.summary?.total_obtainable_score || '-'}`, width: '33.33%' },
            ],
            [
              { text: `POSITION: ${getTermlyPositionText(resultData)}`, width: '33.33%' },
              { text: `SEX: ${resultData?.student?.gender || '-'}`, width: '33.33%' },
              { text: `NEXT TERM BEGINS: ${resultData?.next_term_begins || '-'}`, width: '33.33%' },
            ],
          ].map((row, rowIndex, rowArray) => (
            <View style={styles.tableRow} key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <View
                  style={[
                    rowIndex === rowArray.length - 1 && cellIndex === row.length - 1
                      ? styles.tableCellLastCell
                      : rowIndex === rowArray.length - 1
                      ? styles.tableCellLastRow
                      : cellIndex === row.length - 1
                      ? styles.tableCellLastColumn
                      : styles.tableCell,
                    { width: cell.width },
                  ]}
                  key={cellIndex}
                >
                  <Text>{cell.text}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* Subject Table */}
        <Text style={styles.sectionTitle}>REPORT SHEET FOR {resultData?.class?.category} SECONDARY</Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={styles.tableHeaderSubjectNameCell}>
              <Text>Subject</Text>
            </View>
            {[
              'Test1 (10%)',
              'Test2 (10%)',
              'Assign (10%)',
              'Project (10%)',
              'Exam (60%)',
              'Total Score',
              'Grade',
              'Remark',
              'Position',
              'Highest',
              'Lowest',
              'Average',
            ].map((header, idx) => (
              <View
                style={idx === 11 ? styles.tableHeaderCellLastColumn : styles.tableHeaderCell}
                key={idx}
              >
                <Text>{header}</Text>
              </View>
            ))}
          </View>
          {resultData?.results?.map((subject, idx, array) => (
            <View style={styles.tableRow} key={idx}>
              <View style={idx === array.length - 1 ? styles.subjectNameCellLastRow : styles.subjectNameCell}>
                <Text>{subject.short_name}</Text>
              </View>
              {[
                formatScore(subject.test1) || 0,
                formatScore(subject.test2) || 0,
                formatScore(subject.assignment) || 0,
                formatScore(subject.project) || 0,
                formatScore(subject.exam) || 0,
                formatScore(subject.total_score) || 0,
                subject.grade || '-',
                subject.remark === 'N/A' ? '-' : subject.remark,
                subject.position === '-' ? '-' : formatRank(subject.position),
                formatScore(subject?.evaluation?.highest_score) || '-',
                formatScore(subject?.evaluation?.lowest_score) || '-',
                formatScore(subject?.evaluation?.average) || '-',
              ].map((value, cellIndex) => (
                <View
                  style={[
                    idx === array.length - 1 && cellIndex === 11
                      ? styles.subjectTableCellLastCell
                      : idx === array.length - 1
                      ? styles.subjectTableCellLastRow
                      : cellIndex === 11
                      ? styles.subjectTableCellLastColumn
                      : styles.subjectTableCell,
                  ]}
                  key={cellIndex}
                >
                  <Text>{value}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* Psychomotor Evaluation */}
        <Text style={styles.sectionTitle}>Psychomotor Evaluation</Text>
        <View style={styles.psychomotorGrid}>
          {[
            { name: 'Punctuality', value: resultData?.psychomotor?.punctuality || 'N/A' },
            { name: 'Teamwork', value: resultData?.psychomotor?.teamwork || 'N/A' },
            { name: 'Creativity', value: resultData?.psychomotor?.creativity || 'N/A' },
            { name: 'Leadership Skills', value: resultData?.psychomotor?.leadership_skills || 'N/A' },
            { name: 'Handwriting', value: resultData?.psychomotor?.handwriting || 'N/A' },
            { name: 'Neatness', value: resultData?.psychomotor?.neatness || 'N/A' },
            { name: 'Participation in Activities', value: resultData?.psychomotor?.participation_in_activities || 'N/A' },
            { name: 'Sportsmanship', value: resultData?.psychomotor?.participation || 'N/A' },
            { name: 'Concentration', value: resultData?.psychomotor?.concentration || 'N/A' },
            { name: 'Problem-Solving Skills', value: resultData?.psychomotor?.problem_solving_skills || 'N/A' },
            { name: 'Self-Discipline', value: resultData?.psychomotor?.self_discipline || 'N/A' },
            { name: 'Respect for Others', value: resultData?.psychomotor?.respect_for_others || 'N/A' },
          ].map((item, i) => (
            <View style={styles.psychomotorSkillBox} key={i}>
              <Text style={styles.psychomotorSkillName}>{item.name}</Text>
              <View style={styles.psychomotorSkillValue}>
                <Text>{item.value}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Key Section */}
        <View style={styles.table}>
        {[
            'KEY TO GRADE: F = Fail (0 - 39.9) P = Pass (40 - 59.9) C = Good (60 - 69.9) B = Very Good (70 - 79.9) A = Excellent (80 - 100)',
            'KEY TO RATINGS: 1 = Very Poor 2 = Poor 3 = Fair 4 = Good 5 = Excellent',
        ].map((text, index) => (
            <View style={styles.tableRow} key={index}>
            <View style={index === 1 ? styles.fullWidthTableCellLast : styles.fullWidthTableCell}>
                <Text>{text}</Text>
            </View>
            </View>
        ))}
        </View>

        {/* Stamp */}
        <View style={styles.stamp}>
        <Image src="././images/stamp_optimized.png" style={styles.stampImage} />
        </View>

        {/* Comments and Signatures */}
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.fullWidthTableCell}>
              <Text>Form Teacher's Comment: {resultData?.comment?.form_teacher_comments || '-'}</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.fullWidthTableCell}>
              <Text>Principal's Comment: {resultData?.comment?.principal_comments || '-'}</Text>
            </View>
          </View>
          {/* <View style={styles.tableRow}>
            <View style={styles.fullWidthTableCellLast}>
              <Text>Principal's Signature &amp; Stamp:</Text>
            </View>
          </View> */}
        </View>

        {/* Optional Stamp */}
        <View style={styles.stamp}>
          {/* If you have a stamp image, you can uncomment this */}
          {/* <Image src="/images/stamp.png" style={styles.stampImage} /> */}
        </View>
      </Page>
    ))}
  </Document>
);

export default ClassBatchResultPDF;


